"use client";

import {
  CircleAlert,
  CircleCheck,
  Info,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import type { HTMLAttributes, ReactNode } from "react";

export type AlertTone = "info" | "success" | "warning" | "error";

type AlertProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  icon?: LucideIcon;
};

// Non-color cue per tone so the meaning survives without color.
const alertToneIcons: Record<AlertTone, LucideIcon> = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  error: CircleAlert,
};

/** Single presentation for error, warning, success, and info messages. */
export function Alert({
  tone = "info",
  title,
  children,
  action,
  icon,
  className,
  role,
  ...props
}: AlertProps) {
  const ToneIcon = icon ?? alertToneIcons[tone];
  // A lone sentence reads as the description whichever prop carried it, so a
  // message-only alert never takes the tone colour of a title.
  const heading = children != null ? title : null;
  const message = children ?? title;
  return (
    <div
      {...props}
      // An error alert must always announce assertively.
      role={tone === "error" ? "alert" : (role ?? "status")}
      // Full class names spelled out so the CSS consistency check sees them.
      className={[
        "alert",
        tone === "info" && "alert-info",
        tone === "success" && "alert-success",
        tone === "warning" && "alert-warning",
        tone === "error" && "alert-error",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <ToneIcon className="alert-icon" aria-hidden="true" />
      <div className="alert-body">
        {heading != null && <div className="alert-title">{heading}</div>}
        <div className="alert-description">{message}</div>
        {action != null && <div className="alert-action">{action}</div>}
      </div>
    </div>
  );
}
